import React, { useState, useEffect, useRef } from 'react';
import { useT } from '../i18n';
import { GALLERY_LIMITS, validateProductFields, hasPrice, imageCountOf } from '../constants/gallery';
import { CURRENCIES } from '../constants/designer';
import { PRODUCT_TYPES, PRODUCT_COLORS, subtypesOf } from '../constants/productTaxonomy';

// ── SHARED DIALOG SHELL ──────────────────────────────────────────────────────
// Both gallery dialogs are the same box with different contents, so the overlay,
// Escape handling and click-outside live here once.
function Dialog({ children, onClose, labelledBy, wide = false }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="gald-overlay" onClick={onClose}>
      <div
        className={`gald${wide ? ' gald--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

// ── DESCRIPTION EDITOR ───────────────────────────────────────────────────────
// A real dialog rather than the cramped inline textarea it replaces — 200
// characters need room to read back what you wrote.
//
// The draft is local and only committed on Save, so Cancel genuinely discards.
// The old inline field auto-saved on a debounce, which meant there was no way
// to back out of an edit.
//
// `product` adds a price and a buy link, and is passed only when the owner is a
// SHOP — a shop's photo is a product listing. It is the same dialog because it
// is the same act: saying what this picture is. `onSave` still receives the
// description as its first argument, so the designer path is untouched.
export function DescriptionDialog({
  item, onSave, onClose, product = false,
  // ── THE PICTURE STRIP ─────────────────────────────────────────────────────
  // `shots` is the post's pictures, in order, and is the ONE model both modes
  // use: composing a new post and editing an existing one differ in what a
  // shot is (a draft in memory versus a stored blob) and in what add/remove
  // do (touch local state versus write), never in how the strip looks or
  // behaves. Each shot is:
  //
  //   { key, thumbUrl | null, removable }
  //
  // A null `thumbUrl` is an existing picture whose thumbnail is not loaded —
  // only the COVER's is on the metadata document, and fetching the rest would
  // be a megabyte to open an edit dialog. Those render as numbered placeholders.
  shots = null, onAddImages, onRemoveShot, maxImages = 5, busy = false,
  composing = false,
}) {
  const { t } = useT();
  const [text, setText] = useState(item.description || '');
  // The product's NAME. Stored as `title`, a field the gallery schema has
  // always written and nothing ever filled in — a listing needs a short handle
  // that fits a basket row and a tile caption, which a 200-character
  // description does not.
  const [title, setTitle] = useState(item.title || '');
  const [type, setType] = useState(item.type || '');
  const [subtype, setSubtype] = useState(item.subtype || '');
  const [color, setColor] = useState(item.color || '');
  const [price, setPrice] = useState(item.price == null ? '' : String(item.price));
  const [currency, setCurrency] = useState(item.currency || CURRENCIES[0].code);
  const [productUrl, setProductUrl] = useState(item.productUrl || '');
  // Populated by a failed Save, not by typing: validating on every keystroke
  // means the price is marked invalid the moment the field is focused and
  // empty, which is not an error yet — it is someone about to type.
  const [errors, setErrors] = useState({});
  const ref = useRef(null);
  const priceRef = useRef(null);

  const strip = Array.isArray(shots) ? shots : null;

  // A listing that has never been priced AND already exists — held back from
  // the grid until one is entered, which is what the warning explains and why
  // the left button says "skip" rather than "cancel".
  //
  // NEVER while composing: an unsaved draft is not unpublished, it is unsaved.
  // Telling someone their photo "stays unpublished" before they have made a
  // post would be describing a document that does not exist.
  const unpriced = product && !composing && !hasPrice(item);

  // The cursor starts in the field that is BLOCKING. On an unpriced listing the
  // description is not what the dialog is open for, and landing there means
  // tabbing past it to reach the one thing that has to be filled in.
  useEffect(() => {
    (unpriced ? priceRef : ref).current?.focus();
  }, [unpriced]);

  const save = () => {
    // A POST NEEDS A PICTURE. Only checkable while composing — an existing post
    // has one by definition.
    if (composing && !strip?.length) {
      setErrors({ images: 'gal_err_no_image' });
      return;
    }

    if (!product) {
      onSave(text, null);
      // Composing closes itself once the write succeeds, so that the dialog
      // stays put — and stays filled in — if it fails.
      if (!composing) onClose();
      return;
    }

    const fields = { title, type, subtype, color, price, currency, productUrl };
    const errs = validateProductFields(fields);
    setErrors(errs);
    // A LISTING IS NOT SAVED WITHOUT A PRICE. The dialog stays open on the
    // offending field rather than closing and leaving the item half-filled.
    if (Object.keys(errs).length) return;
    onSave(text, fields);
    if (!composing) onClose();
  };

  return (
    <Dialog onClose={onClose} labelledBy="gald-desc-title" wide={product}>
      <h3 className="gald-title" id="gald-desc-title">
        {composing ? t('gal_new_post')
          : product ? t('gal_menu_details')
            : t('gal_menu_description')}
      </h3>
      <p className="gald-sub">
        {composing ? (product ? t('gal_new_product_sub') : t('gal_new_post_sub')).replace('{max}', maxImages)
          : product ? t('gal_details_sub')
            : t('gal_desc_sub')}
      </p>

      {/* ── TWO COLUMNS ON A DESKTOP ──────────────────────────────────────
          A listing has eight fields, and stacked in one 420px column they ran
          well past the fold on a 900px-tall screen — the Save button was off
          the bottom while the top half of the window sat empty. Split, the
          form fits in view: WHAT IT IS on the left (picture, name,
          description), WHAT IT COSTS on the right (type, colour, price, link).

          One column below 900px, where there is no width to spend and the
          order above is the reading order anyway. Only for a product — a
          designer's dialog is one textarea and has nothing to split. */}
      <div className="gald-grid">
      <div className="gald-col">

      {/* ── THE PICTURES ──────────────────────────────────────────────────
          The post's pictures in order, then a "+" until the fifth. ONE STRIP
          for both modes: composing starts empty, so the "+" stands alone and
          IS the invitation; editing starts with what the post already holds and
          the "+" adds to it. Nothing about the control changes between them —
          only what add and remove do underneath.

          A shot with no thumbnail is an existing picture past the cover: the
          metadata document carries the cover's thumbnail and no others, and
          reading four blobs to fill a strip would be a megabyte spent to open
          a dialog. Those show as numbered placeholders. */}
      {strip && (
        <div className="gald-field gald-field--shots">
          <div className={`gald-shots${errors.images ? ' gald-shots--err' : ''}`}>
            {strip.map((shot, i) => (
              // The cover is marked with a ring rather than a word printed over
              // it. The label covered the corner of a 72px thumbnail — on a
              // strip whose whole job is showing which pictures these are, the
              // one thing that must not be obscured is the picture. The hint
              // under the strip says which one leads, in words, off the image.
              <div className={`gald-shot${i === 0 ? ' gald-shot--cover' : ''}`} key={shot.key}>
                {shot.thumbUrl
                  ? <img src={shot.thumbUrl} alt="" />
                  : (
                    // Still loading. A spinner, not the picture's number: the
                    // tile is about to hold an image, and a digit sitting there
                    // reads as the content rather than as a wait.
                    <span className="gald-shot-load" title={t('gal_shot_n').replace('{n}', i + 1)}>
                      <span className="af-spinner" />
                    </span>
                  )}

                {/* The cover has no remove: every grid in the app shows its
                    thumbnail, which lives on the metadata document, so dropping
                    it would leave those pointing at a picture that is gone.
                    Deleting the post is the way to be rid of it. */}
                {shot.removable && (
                  <button
                    type="button"
                    className="gald-shot-x"
                    onClick={() => onRemoveShot?.(shot)}
                    disabled={busy}
                    aria-label={t('gal_remove_image')}
                    title={t('gal_remove_image')}
                  >✕</button>
                )}

              </div>
            ))}

            {strip.length < maxImages && (
              <button
                type="button"
                className="gald-shot gald-shot--add"
                onClick={onAddImages}
                disabled={busy}
                aria-label={t('gal_add_images')}
                title={t('gal_add_images')}
              >
                {busy ? <span className="af-spinner" /> : <span aria-hidden="true">+</span>}
              </button>
            )}
          </div>
          <span className="gald-hint">
            {t('gal_images_hint').replace('{n}', strip.length).replace('{max}', maxImages)}
          </span>
          {errors.images && <span className="gald-err">{t(errors.images)}</span>}
        </div>
      )}

      {/* THE NAME COMES FIRST, above the description, because it is the thing
          the product is called — everywhere it is listed short (a basket row,
          a feed tile, a shop's grid) this is what shows, and the description is
          the detail behind it. Products only: a designer's photo is work, and
          naming it is not what their gallery is for. */}
      {product && (
        <label className="gald-field gald-field--name">
          <span className="gald-flabel">{t('gal_name')}</span>
          <input
            className="gald-input"
            value={title}
            maxLength={GALLERY_LIMITS.title}
            onChange={e => setTitle(e.target.value)}
            placeholder={t('gal_name_ph')}
          />
        </label>
      )}

      <textarea
        ref={ref}
        className="gald-textarea"
        rows={5}
        value={text}
        maxLength={GALLERY_LIMITS.description}
        onChange={e => setText(e.target.value)}
        placeholder={product ? t('gal_product_desc_ph') : t('gal_desc_ph')}
      />
      <div className="gald-count">{text.length}/{GALLERY_LIMITS.description}</div>

      </div>{/* .gald-col */}

      {product && (
        <div className="gald-product">
          {/* WHY THIS DIALOG IS OPEN, at the top where it is read before the
              fields rather than after them. It appears on both routes in — a
              fresh upload, and pressing "show" on a listing that has no price —
              and in both cases it is the answer to "why isn't this published".
              Gone the moment a price exists, when it would only be noise. */}
          {unpriced && (
            <p className="gald-gate">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v5" /><path d="M12 16.5v.01" />
              </svg>
              <span>{t('gal_price_gate')}</span>
            </p>
          )}
          {/* ── WHAT IT IS ────────────────────────────────────────────────
              Type and subtype, side by side. Two selects rather than one long
              list of every subtype in the app: a hundred options in a single
              dropdown is a scroll, and the pair is how anyone already thinks
              about it — electronics, then TV.

              CHANGING THE TYPE CLEARS THE SUBTYPE. Leaving it would let
              "electronics / parquet" be submitted; the serializer refuses that
              pairing anyway, so without clearing it here the shop would watch a
              filled-in field silently fail to save. */}
          <div className="gald-field">
            <span className="gald-flabel">
              {t('prod_type')} <b className="gald-req">*</b>
            </span>
            <div className="gald-price-row">
              <select
                className={`gald-input${errors.type ? ' gald-input--err' : ''}`}
                value={type}
                onChange={e => {
                  setType(e.target.value);
                  setSubtype('');
                  setErrors(p => ({ ...p, type: '', subtype: '' }));
                }}
                aria-label={t('prod_type')}
              >
                <option value="">{t('prod_type_ph')}</option>
                {PRODUCT_TYPES.map(k => (
                  <option key={k} value={k}>{t(`shop_cat_${k}`)}</option>
                ))}
              </select>

              <select
                className={`gald-input${errors.subtype ? ' gald-input--err' : ''}`}
                value={subtype}
                onChange={e => { setSubtype(e.target.value); setErrors(p => ({ ...p, subtype: '' })); }}
                // Nothing to choose from until a type is picked, and an open
                // dropdown with one placeholder in it invites a click that
                // does nothing.
                disabled={!type}
                aria-label={t('prod_subtype')}
              >
                <option value="">{type ? t('prod_subtype_ph') : '—'}</option>
                {subtypesOf(type).map(k => (
                  <option key={k} value={k}>{t(`prod_sub_${k}`)}</option>
                ))}
              </select>
            </div>
            {errors.type && <span className="gald-err">{t(errors.type)}</span>}
            {errors.subtype && <span className="gald-err">{t(errors.subtype)}</span>}
          </div>

          {/* ── COLOUR ────────────────────────────────────────────────────
              Swatches, not a dropdown: colour is the one attribute you
              recognise faster than you read, and a row of circles is scanned
              in a glance where a list of colour names has to be parsed.

              A FIXED PALETTE — see productTaxonomy.js. `multicolor` is what
              makes requiring this fair for a patterned rug. */}
          <div className="gald-field">
            <span className="gald-flabel">
              {t('prod_color')} <b className="gald-req">*</b>
              {color && <span className="gald-opt">{t(`prod_color_${color}`)}</span>}
            </span>
            <div className={`gald-colors${errors.color ? ' gald-colors--err' : ''}`} role="radiogroup" aria-label={t('prod_color')}>
              {PRODUCT_COLORS.map(c => (
                <button
                  key={c.id}
                  type="button"
                  role="radio"
                  aria-checked={color === c.id}
                  className={`gald-color${color === c.id ? ' gald-color--on' : ''}${c.hex ? '' : ' gald-color--multi'}`}
                  style={c.hex ? { '--sw': c.hex } : undefined}
                  onClick={() => { setColor(c.id); setErrors(p => ({ ...p, color: '' })); }}
                  aria-label={t(`prod_color_${c.id}`)}
                  title={t(`prod_color_${c.id}`)}
                />
              ))}
            </div>
            {errors.color && <span className="gald-err">{t(errors.color)}</span>}
          </div>

          <label className="gald-field">
            <span className="gald-flabel">
              {t('gal_price')} <b className="gald-req">*</b>
            </span>
            <div className="gald-price-row">
              <input
                ref={priceRef}
                className={`gald-input${errors.price ? ' gald-input--err' : ''}`}
                type="number" inputMode="decimal" min="0" step="any"
                value={price}
                onChange={e => { setPrice(e.target.value); setErrors(p => ({ ...p, price: '' })); }}
                placeholder={t('gal_price_ph')}
                aria-invalid={!!errors.price}
                required
              />
              <select
                className="gald-input gald-currency"
                value={currency}
                onChange={e => setCurrency(e.target.value)}
                aria-label={t('ds_currency')}
              >
                {CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                ))}
              </select>
            </div>
            {errors.price && <span className="gald-err">{t(errors.price)}</span>}
          </label>

          <label className="gald-field">
            <span className="gald-flabel">
              {t('gal_product_link')} <span className="gald-opt">{t('ds_optional')}</span>
            </span>
            <input
              className={`gald-input${errors.productUrl ? ' gald-input--err' : ''}`}
              type="url" inputMode="url"
              value={productUrl}
              maxLength={GALLERY_LIMITS.productUrl}
              onChange={e => { setProductUrl(e.target.value); setErrors(p => ({ ...p, productUrl: '' })); }}
              placeholder="https://shop.com/product"
              aria-invalid={!!errors.productUrl}
            />
            {errors.productUrl && <span className="gald-err">{t(errors.productUrl)}</span>}
          </label>

          {/* Only once the gate above is gone — two notes about the same form
              is one too many, and this is the lesser of them. */}
          {!unpriced && <p className="gald-hint">{t('gal_link_optional')}</p>}
        </div>
      )}

      </div>{/* .gald-grid */}

      <div className="gald-actions">
        {/* "Skip" rather than "Cancel" on an unpriced listing: there is nothing
            to cancel — the photo is uploaded — and the button leaves it sitting
            unpublished rather than undoing anything. */}
        <button type="button" className="gald-btn" onClick={onClose} disabled={busy}>
          {unpriced ? t('gal_skip') : t('cancel')}
        </button>
        <button
          type="button"
          className="gald-btn gald-btn--primary"
          onClick={save}
          // Nothing to post without a picture, and while files are still
          // compressing the draft is not yet what it will be.
          disabled={busy || (composing && !strip?.length)}
        >
          {busy && <span className="af-spinner" />}
          {composing ? t('gal_post') : t('gal_save')}
        </button>
      </div>
    </Dialog>
  );
}

// ── DELETE CONFIRMATION ──────────────────────────────────────────────────────
// Replaces window.confirm, which can't be styled, can't be translated, and on
// some mobile browsers is suppressed entirely — which would have made deletion
// silently do nothing.
export function ConfirmDialog({ title, message, confirmLabel, onConfirm, onClose }) {
  const { t } = useT();
  return (
    <Dialog onClose={onClose} labelledBy="gald-confirm-title">
      <h3 className="gald-title" id="gald-confirm-title">{title}</h3>
      <p className="gald-sub">{message}</p>
      <div className="gald-actions">
        <button type="button" className="gald-btn" onClick={onClose}>{t('gal_no')}</button>
        <button
          type="button"
          className="gald-btn gald-btn--danger"
          onClick={() => { onConfirm(); onClose(); }}
        >{confirmLabel || t('gal_yes')}</button>
      </div>
    </Dialog>
  );
}
